import {Box, Button, FormControl, MenuItem, Select, SelectChangeEvent, Typography} from "@mui/material";
import {ChevronLeft, ChevronRight} from "@mui/icons-material";
import {RTL} from "../../cache/CacheRtl";
import {colors, borders} from "./colors";
import {t} from "../dashboard/coach/coachTokens";

type PageToken = number | "...";

interface PaginationProps {
  /** Zero-based index of the current page. */
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange: (page: number) => void;
  /** When provided, renders the rows-per-page selector. */
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  itemsPerPageOptions?: number[];
}

const Pagination = ({
                      currentPage = 0,
                      totalPages = 0,
                      totalItems = 0,
                      itemsPerPage = 20,
                      onPageChange,
                      onItemsPerPageChange,
                      itemsPerPageOptions = [5, 10, 20, 50],
                    }: PaginationProps) => {
  if (totalPages <= 0) return null;

  const start = currentPage * itemsPerPage + 1;
  const end = Math.min((currentPage + 1) * itemsPerPage, totalItems);

  const getPageNumbers = (): PageToken[] => {
    const pages: PageToken[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      pages.push(0);
      if (currentPage > 2) pages.push("...");
      const rangeStart = Math.max(1, currentPage - 1);
      const rangeEnd = Math.min(totalPages - 2, currentPage + 1);
      for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
      if (currentPage < totalPages - 3) pages.push("...");
      pages.push(totalPages - 1);
    }

    return pages;
  };

  return (
      <RTL>
        <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "12px 16px",
              borderTop: borders.default,
              direction: "rtl",
              flexWrap: "wrap",
              gap: 1.5,
            }}
        >
          <Typography sx={{fontSize: "13px", color: colors.textSecondary, whiteSpace: "nowrap"}}>
            מציג {start}-{end} מתוך {totalItems}
          </Typography>

          <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
            <Button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 0}
                size="small"
                sx={{
                  minWidth: "32px",
                  height: "32px",
                  padding: 0,
                  color: colors.textSecondary,
                  "&:disabled": {color: colors.borderHover},
                }}
            >
              <ChevronLeft fontSize="small"/>
            </Button>

            {getPageNumbers().map((page, index) =>
                page === "..." ? (
                    <Typography key={`ellipsis-${index}`} sx={{fontSize: "13px", color: colors.textMuted, px: 0.5}}>
                      ...
                    </Typography>
                ) : (
                    <Button
                        key={page}
                        onClick={() => onPageChange(page)}
                        size="small"
                        sx={{
                          minWidth: "32px",
                          height: "32px",
                          padding: 0,
                          fontSize: "13px",
                          fontWeight: currentPage === page ? 700 : 500,
                          borderRadius: borders.radius.sm,
                          backgroundColor: currentPage === page ? t.green : "transparent",
                          color: currentPage === page ? colors.white : colors.textSecondary,
                          border: currentPage === page ? "none" : borders.default,
                          "&:hover": {
                            backgroundColor: currentPage === page ? t.greenDark : colors.bg,
                          },
                        }}
                    >
                      {page + 1}
                    </Button>
                )
            )}

            <Button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage >= totalPages - 1}
                size="small"
                sx={{
                  minWidth: "32px",
                  height: "32px",
                  padding: 0,
                  color: colors.textSecondary,
                  "&:disabled": {color: colors.borderHover},
                }}
            >
              <ChevronRight fontSize="small"/>
            </Button>
          </Box>

          {onItemsPerPageChange && (
              <Box sx={{display: "flex", alignItems: "center", gap: 1, direction: "rtl", flexDirection: "row-reverse"}}>
                <FormControl size="small">
                  <Select
                      value={itemsPerPage}
                      onChange={(e: SelectChangeEvent<number>) => onItemsPerPageChange(Number(e.target.value))}
                      sx={{
                        height: "32px",
                        fontSize: "13px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.border,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.borderHover,
                        },
                      }}
                  >
                    {itemsPerPageOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography sx={{fontSize: "13px", color: colors.textSecondary, whiteSpace: "nowrap"}}>
                  שורות בעמוד:
                </Typography>
              </Box>
          )}
        </Box>
      </RTL>
  );
};

export default Pagination;
